import type { BlockType } from '@unifyapps/defs/types/block';
import { useEffect, useMemo } from 'react';
import { getIsDataSourceAnEntityType } from '../../../utils/entityDataSource';
import DependencyFlowHelper from '../../../stores/GlobalStateStore/helpers/DependencyFlowHelper';
import { useGetDataSourceRecordStoreState } from '../../../stores/DataSourceRecordStore';
import DataSourceHelper from '../../../helper/DataSourceHelper';
import { useGetGlobalStateStoreState, useGlobalStateStore } from '../../../stores/GlobalStateStore';

export function useTriggerAutomaticDataSources({
  dataSourceIds = [],
}: {
  dataSourceIds: BlockType['dataSourceIds'];
}) {
  const { getGlobalStateStoreState } = useGetGlobalStateStoreState();
  const { getDataSourceRecordStoreState } = useGetDataSourceRecordStoreState();
  const { datasource } = useGlobalStateStore().use.actions();

  const automaticDataSourceIds = useMemo(() => {
    const dataSourceRecords = getDataSourceRecordStoreState().dataSources;
    // it is possible that we might have includes manual data source id in dataSourceIds
    return dataSourceIds.filter((id) => {
      // If a data source is accessed somewhere but is later deleted.
      if (dataSourceRecords[id]) {
        return !DataSourceHelper.isManualDataSource(dataSourceRecords[id]);
      }

      // If the data source is of entity type, include it to trigger.
      // This ensures proper handling for cases like Data Table with an entity as source.
      if (getIsDataSourceAnEntityType(id)) {
        return true;
      }

      return false;
    });
  }, [dataSourceIds, getDataSourceRecordStoreState]);

  const nonStagedDataSourceIds = useMemo(() => {
    const dsDependencyFlow = getGlobalStateStoreState().dependencyFlowManager.dataSources;
    return automaticDataSourceIds.filter((id) => {
      return DependencyFlowHelper.isDependencyUnstaged(dsDependencyFlow, id);
    });
  }, [automaticDataSourceIds, getGlobalStateStoreState]);

  useEffect(() => {
    if (nonStagedDataSourceIds.length) {
      datasource.addDependencyFlows(
        DependencyFlowHelper.createStagingDependencyFlow({
          ids: nonStagedDataSourceIds,
        }),
      );
    }
  }, [datasource, nonStagedDataSourceIds]);
}
